import { Component, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FileRemoveEvent, FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';

import { FeedbackFileInfo, NewSupportHubRequest } from '../support-hub.models';
import { SupportHubStore } from '../support-hub.store';
import { MfgappsEventService } from '../../../services/mfgapps-event.service';
import { MfgappsApiService } from '../../../services/mfgapps-api.service';

type Shop = {
  shop_id: number;
  shop_name: string;
};

type Plant = {
  plant_id: number;
  plant_name: string;
  shopList: Shop[];
};

type Company = {
  company_id: number;
  company_code: string;
  plantList: Plant[];
};

type UserPreferencesResult = {
  corporate_id: number | null;
  corporate: string | null;
  company_id: number | null;
  company: string | null;
  plant_id: number | null;
  plant: string | null;
  shop_id: number | null;
  shop: string | null;
};

@Component({
  selector: 'app-support-hub-submit',
  standalone: true,
  imports: [FormsModule, MatFormFieldModule, MatSelectModule, FileUploadModule, MatProgressSpinnerModule],
  templateUrl: './support-hub-submit.component.html',
  styleUrl: './support-hub-submit.component.scss'
})
export class SupportHubSubmitComponent {
  @ViewChild(FileUpload) fileUploader!: FileUpload;
  submitted = false;
  loader = { isLoading: false, message: '' };
  submitError: string | null = null;
  companyList: Company[] = [];
  currentCompany: Company | null = null;
  currentPlant: Plant | null = null;
  isEditMode = false;
  feedbackFiles: FeedbackFileInfo[] = [];
  disableusecase = false;
  
  request: NewSupportHubRequest = {
    title: '',
    description: '',
    requested_user_name: '',
    creation_date: '',
    corporate_id: null,
    company_id: null,
    plant_id: null,
    shop_id: null,
    company: '',
    plant: '',
    shop: '',
    usecase_id: 0,
    workcategory_id: null,
    workcategorytype_id: null,
    application_id: null,
    rank_id: null,
    status_id: null,
    user_id: 0
  };

  get useCaseOptions() {
    return this.store.useCaseOptions;
  }

  get criticalityOptions() {
    return this.store.criticalityOptions;
  }

  get applicationOptions() {
    if (!this.request.usecase_id) {
      return [];
    }
    const useCase = this.store.useCaseOptions.find(u => u.id === this.request.usecase_id);
    return useCase?.applications ?? [];
  }

  get workCategoryOptions() {
    if (!this.request.usecase_id) {
      return [];
    }
    return this.store.getWorkCategoryOptionsForUseCaseId(this.request.usecase_id);
  }

  get selectedWorkCategoryName(): string {
    return this.store.getWorkCategoryNameById(this.request.workcategory_id);
  }

  get requestTypeOptions() {
    if (!this.request.usecase_id || !this.request.workcategory_id) {
      return [];
    }
    const useCase = this.store.useCaseOptions.find(u => u.id === this.request.usecase_id);
    if (!useCase) {
      return [];
    }
    return useCase.workcategories
      .filter(item => item.wcId === this.request.workcategory_id)
      .map(item => ({ id: item.wctId, name: item.wctName, desc: '' }));
  }

  get isCompanyRequired(): boolean {
    return !!this.request.usecase_id;
  }

  get isPlantRequired(): boolean {
    if (!this.request.usecase_id) {
      return false;
    }
    const useCaseName = this.store.getUseCaseNameById(this.request.usecase_id);
    return this.store.requiresPlantSelection(useCaseName);
  }

  get isShopRequired(): boolean {
    if (!this.request.usecase_id) {
      return false;
    }
    const useCaseName = this.store.getUseCaseNameById(this.request.usecase_id);
    return this.store.requiresShopSelection(useCaseName);
  }

  get canSubmit(): boolean {
    if (!this.request.title.trim() || !this.request.description.trim() || !this.request.requested_user_name.trim()) {
      return false;
    }
    if (!this.request.usecase_id || !this.request.workcategory_id || !this.request.workcategorytype_id || !this.request.application_id) {
      return false;
    }
    if (this.isCompanyRequired && !this.request.company_id) {
      return false;
    }
    if (this.isPlantRequired && !this.request.plant_id) {
      return false;
    }
    if (this.isShopRequired && !this.request.shop_id) {
      return false;
    }
    return true;
  }

  private setLoader(message: string): void {
    this.loader.isLoading = true;
    this.loader.message = message;
  }

  private resetLoader(): void {
    this.loader.isLoading = false;
    this.loader.message = '';
  }

  private  isusecaseDisabled(): boolean { 
    return !this.eventService.isDevelopmentEnvironment;
  }
  
  
  constructor(
    public readonly store: SupportHubStore,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly eventService: MfgappsEventService,
    private readonly apiService: MfgappsApiService
  ) {}

  async ngOnInit() {
    const requestId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!requestId;
    this.request.usecase_id = this.store.selectedUseCaseIdValue ?? 0;

    if (this.isEditMode && requestId) {
      this.request.feedback_id = Number(requestId);
      await this.loadEditRequest(requestId);
    }
    else {
      this.request.requested_user_name = this.store.currentUser;
      const criticalityOptions = this.store.criticalityOptions;
      if (criticalityOptions.length > 0) {
        this.request.rank_id = criticalityOptions[0].id;
      }
      this.request.status_id = this.store.statusIds.NEW;
      if (this.request.usecase_id) {
        await this.applyUserPreferencesForUseCase(this.request.usecase_id);
      }
    }

    this.companyList = (await this.eventService.getCorporateHierarchy()) as Company[];
    if (this.request.company_id) {
      this.currentCompany = this.companyList.find(c => c.company_id === this.request.company_id) ?? null;
    }
    if (this.request.plant_id && this.currentCompany) {
      this.currentPlant = this.currentCompany.plantList.find(p => p.plant_id === this.request.plant_id) ?? null;
    }
    this.disableusecase = this.isusecaseDisabled();
  }

  private async loadEditRequest(requestId: string): Promise<void> {
    this.setLoader('Loading ticket details...');
    try {
      const response = await this.apiService.getFeedbackEntryDetails(requestId);
      if (response?.result) {
        this.request = {
          ...response.result
        };
        this.feedbackFiles = [...(response.result.files ?? [])];
      }
    } finally {
      this.resetLoader();
    }
  }

  backToList(): void {
    this.router.navigate(['/support-hub/all-tickets'], {
      queryParams: this.store.getUseCaseQueryParams()
    });
  }

  onUseCaseChange(useCaseId: number | null) {
    this.request.workcategory_id = null;
    this.request.workcategorytype_id = null;
    this.request.application_id = null;
    if (!this.isPlantRequired) {
      this.currentPlant = null;
      this.request.company_id = null;
      this.request.plant_id = null;
      this.request.shop_id = null;
      this.request.plant = '';
      this.request.shop = '';
    }
    if (!this.isShopRequired) {
      this.request.shop_id = null;
      this.request.shop = '';
    }
    if (useCaseId) {
      void this.applyUserPreferencesForUseCase(useCaseId);
    }
  }

  private async applyUserPreferencesForUseCase(useCaseId: number): Promise<void> {
    this.setLoader('Loading your preferences...');
    try {
      const response = await this.apiService.getUserPreferences(useCaseId);
      const result = response?.result as UserPreferencesResult | undefined;
      if (!result) {
        return;
      }
      this.onCompanyChange(result.company_id ?? null);
      if (!result.company_id) {
        return;
      }
      this.onPlantChange(result.plant_id ?? null);
      if (!result.plant_id) {
        return;
      }
      this.onShopChange(result.shop_id ?? null);
    } 
    catch {
      // Keep form editable with existing defaults when preferences API fails.
    } 
    finally {
      this.resetLoader();
    }
  }

  onWorkCategoryChange(categoryId: number) {
    this.request.workcategory_id = categoryId;
    this.request.workcategorytype_id = null;
    this.request.application_id = null;
  }

  onCompanyChange(companyId: number | null) {
    this.request.company_id = companyId;
    this.currentCompany = this.companyList.find((company: Company) => company.company_id === companyId) ?? null;
    this.currentPlant = null;
    this.request.plant_id = null;
    this.request.shop_id = null;
    this.request.company = this.currentCompany?.company_code ?? '';
    this.request.plant = '';
    this.request.shop = '';
  }

  onPlantChange(plantId: number | null) {
    this.request.plant_id = plantId;
    this.currentPlant = this.currentCompany?.plantList.find((plant: Plant) => plant.plant_id === plantId) ?? null;
    this.request.shop_id = null;
    this.request.plant = this.currentPlant?.plant_name ?? '';
    this.request.shop = '';
  }

  onShopChange(shopId: number | null) {
    this.request.shop_id = shopId;
    const currentShop = this.currentPlant?.shopList.find((shop: Shop) => shop.shop_id === shopId);
    this.request.shop = currentShop?.shop_name ?? '';
  }

  onCriticalityChange(criticalityId: number | null) {
    this.request.rank_id = criticalityId;
  }

  onRequestTypeChange(requestTypeId: number | null) {
    this.request.workcategorytype_id = requestTypeId;
    this.request.application_id = null;
  }

  onApplicationChange(applicationId: number | null) {
    this.request.application_id = applicationId;
  }

  onAttachmentRemoved(event: FileRemoveEvent): void {
    //Update feedbackFiles array if files are locally removed from file upload component
    this.feedbackFiles = this.feedbackFiles.filter(f => f.file !== event.file);
  }

  async onAttachmentSelected(event: FileSelectEvent): Promise<void> {
    if (!this.loader.isLoading && event.files.length > 0) {
      this.setLoader('Uploading attachments...');
      try {
        await this.store.handleAttachments(this.fileUploader,
          event.files, this.feedbackFiles, this.request.feedback_id);
      }
      finally {
        this.resetLoader();
      }
    }
  }

  private async uploadPendingNewSubmissionAttachments(): Promise<boolean> {
    const localEntries = this.feedbackFiles.filter(f => !!f.file);
    if (localEntries.length === 0) {
      return true;
    }
    this.feedbackFiles = this.feedbackFiles.filter(f => !f.file);
    this.setLoader('Uploading attachments...');
    try {
      return this.store.uploadAttachmentsInParallel(localEntries, false);
    }
    finally {
      this.resetLoader();
    }
  }

  async downloadAttachment(index: number): Promise<void> {
    if (!this.loader.isLoading && this.feedbackFiles[index]) {
      this.setLoader('Downloading attachment...');
      try {
        await this.store.downloadAttachment(this.feedbackFiles[index]);
      }
      finally {
        this.resetLoader();
      }
    }
  }

  async deleteAttachment(index: number): Promise<void> {
    if (!this.loader.isLoading && this.feedbackFiles[index]) {
      this.setLoader('Deleting attachment...');
      try {
        if (await this.store.deleteAttachment(this.feedbackFiles[index])) {
          this.feedbackFiles = this.feedbackFiles.filter((_, i) => i !== index);
        }
      }
      finally {
        this.resetLoader();
      }
    }
  }

  async addOrUpdateRequest() {
    if (!this.canSubmit || this.loader.isLoading) {
      return;
    }
    this.submitError = null;
    try {
      if (!this.request.feedback_id) {
        if (!await this.submitFeedbackRequest()) {
          return;
        }
        if (!await this.uploadPendingNewSubmissionAttachments()) {
          return;
        }
        this.eventService.showSnackBar('Request submitted successfully !', 'success');
        this.redirectAfterSaving();
      }
      else if (await this.updateFeedbackRequest()) {
        this.eventService.showSnackBar('Request updated successfully !', 'success');
        this.redirectAfterSaving();
      }
    }
    finally {
      this.resetLoader();
    }
  }

  private redirectAfterSaving() {
    this.submitted = true;
    this.setLoader('Navigating to ticket detail');
    //After saving new or updated request, redirect to details page
    setTimeout(() => {
      this.router.navigate(['/support-hub/request/detail', this.request.feedback_id]);
    }, 1000);
  }

  private async submitFeedbackRequest(): Promise<boolean> {
    try {
      this.request.corporate_id = this.eventService.corporateId;
      this.request.user_id = Number(this.eventService.getActiveAccount()?.workdayID ?? 0);
      this.setLoader('Submitting new ticket...');
      const response = await this.apiService.submitFeedbackEntry(this.request);
      if (response?.message !== 'Success' || !response?.result?.feedback_id) {
        const errorMessage = 'Submission failed: unexpected response from server. Please try again.';
        this.submitError = errorMessage;
        this.eventService.showSnackBar(errorMessage, 'error');
      }
      else {
        this.request.feedback_id = response.result.feedback_id;
        this.feedbackFiles.forEach(f => f.feedback_id = this.request.feedback_id ?? 0);
        this.location.replaceState(`/support-hub/request/edit/${this.request.feedback_id}`);
      }
    }
    catch (error: any) {
      const errorMessage = error.message ?? 'An error occurred while submitting your request. Please try again.';
      this.submitError = errorMessage;
      this.eventService.showSnackBar(errorMessage, 'error');
    }
    return this.submitError == null;
  }

  private async updateFeedbackRequest(): Promise<boolean> {
    try {
      this.setLoader('Updating ticket...');
      const response = await this.apiService.updateFeedbackEntry(this.request.feedback_id ?? 0, this.request);
      if (response?.message !== 'Success') {
        const errorMessage = 'Update failed: unexpected response from server. Please try again.';
        this.submitError = errorMessage;
        this.eventService.showSnackBar(errorMessage, 'error');
      }
    } 
    catch (error: any) {
      const errorMessage = error.message ?? 'An error occurred while updating your request. Please try again.';
      this.submitError = errorMessage;
      this.eventService.showSnackBar(errorMessage, 'error');
    }
    return this.submitError == null;
  }
}